import React, { FC, createContext, useContext, useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const EASE_EXPAND = [0.19, 1, 0.22, 1]

// Root Accordion Context
interface AccordionContextProps {
  expandedValues: string[]
  toggleValue: (value: string) => void
  border?: boolean
  hover?: boolean
  toggle?: boolean
}

const AccordionContext = createContext<AccordionContextProps | null>(null)

// Item Context
interface AccordionItemContextProps {
  value: string
  isExpanded: boolean
  disabled?: boolean
  border?: boolean
  hover?: boolean
  toggle?: boolean
}

const AccordionItemContext = createContext<AccordionItemContextProps | null>(null)

export interface AccordionProps {
  type?: 'single' | 'multiple'
  defaultValue?: string | string[]
  value?: string | string[]
  onValueChange?: (val: any) => void
  border?: boolean
  hover?: boolean
  toggle?: boolean
  className?: string
  children?: React.ReactNode
}

export const Accordion: FC<AccordionProps> = ({
  type = 'single',
  defaultValue,
  value: controlledValue,
  onValueChange,
  border = false,
  hover = false,
  toggle = true,
  className = '',
  children,
}) => {
  const getInitialValues = (): string[] => {
    if (controlledValue !== undefined) {
      return Array.isArray(controlledValue) ? controlledValue : [controlledValue]
    }
    if (defaultValue !== undefined) {
      return Array.isArray(defaultValue) ? defaultValue : [defaultValue]
    }
    return []
  }

  const [expandedValues, setExpandedValues] = useState<string[]>(getInitialValues)

  useEffect(() => {
    if (controlledValue !== undefined) {
      setExpandedValues(Array.isArray(controlledValue) ? controlledValue : [controlledValue])
    }
  }, [controlledValue])

  const toggleValue = (itemValue: string) => {
    let nextValues: string[] = []
    if (type === 'single') {
      nextValues = expandedValues.includes(itemValue) ? [] : [itemValue]
    } else {
      nextValues = expandedValues.includes(itemValue)
        ? expandedValues.filter((v) => v !== itemValue)
        : [...expandedValues, itemValue]
    }

    setExpandedValues(nextValues)
    if (onValueChange) {
      onValueChange(type === 'single' ? (nextValues[0] || '') : nextValues)
    }
  }

  return (
    <AccordionContext.Provider
      value={{
        expandedValues,
        toggleValue,
        border,
        hover,
        toggle,
      }}
    >
      <div className={`w-full font-google-sans ${className}`}>{children}</div>
    </AccordionContext.Provider>
  )
}

export interface AccordionItemProps {
  value: string
  disabled?: boolean
  border?: boolean
  hover?: boolean
  toggle?: boolean
  className?: string
  children?: React.ReactNode
}

export const AccordionItem: FC<AccordionItemProps> = ({
  value,
  disabled = false,
  border: itemBorder,
  hover: itemHover,
  toggle: itemToggle,
  className = '',
  children,
}) => {
  const rootCtx = useContext(AccordionContext)
  const isExpanded = rootCtx ? rootCtx.expandedValues.includes(value) : false

  const border = itemBorder !== undefined ? itemBorder : rootCtx?.border ?? false
  const hover = itemHover !== undefined ? itemHover : rootCtx?.hover ?? false
  const toggle = itemToggle !== undefined ? itemToggle : rootCtx?.toggle ?? true

  const borderStyles = border
    ? 'border-b border-outline-base/80'
    : ''

  const disabledStyles = disabled
    ? 'opacity-50 cursor-not-allowed pointer-events-none'
    : 'cursor-pointer'

  const handleToggle = (e: React.SyntheticEvent) => {
    e.preventDefault()
    if (!disabled && rootCtx) {
      rootCtx.toggleValue(value)
    }
  }

  return (
    <AccordionItemContext.Provider
      value={{
        value,
        isExpanded,
        disabled,
        border,
        hover,
        toggle,
      }}
    >
      <div
        onClick={handleToggle}
        className={`w-full group select-none transition-all duration-600 ease-in-out ${borderStyles} ${disabledStyles} ${className}`}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  )
}

export interface AccordionTriggerProps {
  children: React.ReactNode
  className?: string
}

export const AccordionTrigger: FC<AccordionTriggerProps> = ({
  children,
  className = '',
}) => {
  const itemCtx = useContext(AccordionItemContext)
  if (!itemCtx) return null

  const { isExpanded, disabled, border, hover, toggle } = itemCtx

  const paddingInlineStyles: React.CSSProperties = border
    ? {
        paddingTop: 'var(--spacing-pad-base, 16px)',
        paddingBottom: 'var(--spacing-pad-base, 16px)',
        paddingLeft: 'var(--spacing-pad-base, 16px)',
        paddingRight: 'var(--spacing-pad-base, 16px)',
      }
    : {
        paddingTop: 'var(--spacing-pad-small, 12px)',
        paddingBottom: 'var(--spacing-pad-small, 12px)',
      }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-expanded={isExpanded}
      style={paddingInlineStyles}
      className={`w-full flex items-center justify-between text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-base transition-all duration-300 ${className}`}
    >
      <span
        style={{ fontSize: 'var(--font-size-trigger, 1rem)' }}
        className={`font-bold lowercase transition-all duration-300 ease-in-out pointer-events-none ${
          isExpanded ? 'text-primary' : 'text-on-surface'
        } ${hover && !disabled ? 'group-hover:underline group-hover:text-primary' : ''}`}
      >
        {children}
      </span>
      {toggle && (
        <div className="w-4 h-4 flex items-center justify-center shrink-0 ml-4 pointer-events-none">
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.6, ease: EASE_EXPAND }}
            className="w-4 h-4 flex items-center justify-center"
          >
            <svg
              className={`w-4 h-4 transition-colors duration-600 ease-in-out ${
                isExpanded ? 'text-primary' : 'text-outline-high'
              } ${hover && !disabled ? 'group-hover:text-primary' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
            </svg>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export interface AccordionContentProps {
  children: React.ReactNode
  className?: string
}

export const AccordionContent: FC<AccordionContentProps> = ({
  children,
  className = '',
}) => {
  const itemCtx = useContext(AccordionItemContext)
  if (!itemCtx) return null

  const { isExpanded, border } = itemCtx

  const contentPaddingStyles: React.CSSProperties = border
    ? {
        paddingLeft: 'var(--spacing-pad-base, 16px)',
        paddingRight: 'var(--spacing-pad-base, 16px)',
        paddingBottom: 'var(--spacing-pad-base, 16px)',
        paddingTop: '0px',
      }
    : {
        paddingBottom: 'var(--spacing-pad-small, 12px)',
        paddingTop: '4px',
      }

  return (
    <AnimatePresence initial={false}>
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.6, ease: EASE_EXPAND }}
          className="overflow-hidden cursor-pointer"
        >
          <div
            style={{
              ...contentPaddingStyles,
              fontSize: 'var(--font-size-content, 0.875rem)',
            }}
            className={`text-on-surface-variant font-google-sans leading-relaxed transition-all duration-300 ${className}`}
          >
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default Accordion
